import "./styles.css";

import {
  Button,
  Card,
  Col,
  DatePicker,
  Image,
  notification,
  Row,
  Space,
  Tag,
  theme,
  Tooltip,
  Typography,
} from "antd";
import { RangePickerProps } from "antd/lib/date-picker";
import dayjs from "dayjs";
import { useMemo, useState } from "react";

import {
  CaretRightOutlined,
  PaperClipOutlined,
  SaveTwoTone,
} from "@ant-design/icons";

import UserRequestCardProps from "../../../../propInterfaces/userRequestCardProps";
import {
  playManagerTrip,
  updateDescription,
  updateSheduleDate,
} from "../../../../services/userRequestApi";
import { CardItem } from "../../../../shared/components/CardImageText";
import {
  Calendar,
  CarIcon,
  CompanyIcon,
  HashtagIcon,
  HotelIcon,
  KmIcon,
  MobileIcon,
  NoteIcon,
  ProviderIcon,
  UserIcon,
} from "../../../../shared/constants/Images";
import {
  AntDesignWhite,
  IconDarkMode,
} from "../../../../shared/constants/Resources";
import { StatusTranslations } from "../../../../shared/constants/Status";
import { TRIP_STATUS } from "../../../../shared/constants/TripStatus";
import { generateClipboardText } from "../../../../utils/helpers/generateClipboardText";
import { sortDestinations } from "../../../../utils/helpers/sortDestinations";
import CreateEmptyTrip from "../../../emptyTrip/form/createEmptyTrip";
import EmptyTripDetails from "../../../emptyTrip/list/emptyTripDatails";
import RouteSummary from "./RouteSummary";
import TripExtraDetails from "./TripExtraDetails";
import { getBackgroundByStatus } from "../utils";

const { Paragraph, Text } = Typography;
const DEFAULT_DESCRIPTION = "No hay notas";

const disabledDate: RangePickerProps["disabledDate"] = (current) => {
  return current < dayjs().endOf("day").subtract(1, "day");
};

export const UserRequestCard = ({
  userRequest,
  isChecked,
  onSelect,
}: UserRequestCardProps) => {
  const {
    token: { colorText },
  } = theme.useToken();
  const showPartner =
    userRequest.qrFee && parseFloat(userRequest.qrFee.toString()) > 0;
  const showInternational =
    userRequest?.country && userRequest?.country.name !== "Uruguay";
  const isTextDarkMode = colorText === AntDesignWhite;
  const iconFilter = isTextDarkMode ? IconDarkMode : "";
  const [loading, setLoading] = useState(false);
  const [editableDescription, setEditableDescription] = useState(
    userRequest.description || DEFAULT_DESCRIPTION,
  );
  const [scheduleDate, setScheduleDate] = useState(
    dayjs(userRequest.scheduleAt),
  );
  const playableStatus = [
    TRIP_STATUS.Searching,
    TRIP_STATUS.Created,
    TRIP_STATUS.Scheduled,
    TRIP_STATUS.Started,
    TRIP_STATUS.Accepted,
  ];

  const distinctPassengers = Array.from(
    new Map(userRequest?.passengers?.map((item) => [item.name, item])).values(),
  );

  const passengerPhone = userRequest.passengers
    ? userRequest.passengers[0]?.phone
    : userRequest?.user?.mobile;

  const userRequestDtoStatus = useMemo(() => {
    return (
      StatusTranslations.find(
        (statusTranslation) => statusTranslation.name === userRequest.status,
      )?.translation ?? ""
    );
  }, [userRequest.status]);

  const backgroundColor = useMemo(() => {
    return getBackgroundByStatus(userRequestDtoStatus);
  }, [userRequestDtoStatus]);

  const isLote = userRequest?.passengersData?.length! > 1;

  const destinationAddress = userRequest.destinations?.length
    ? sortDestinations(userRequest.destinations)[
        userRequest.destinations.length - 1
      ].address!
    : userRequest?.destinationAddress!;

  const saveDescription = async () => {
    if (!userRequest.id) {
      return;
    }

    try {
      await updateDescription(userRequest.id, editableDescription);
      notification.success({
        message: "Descripción actualizada correctamente!",
        placement: "bottom",
        key: "global",
      });
    } catch (error) {
      notification.error({
        message: "Ha ocurrido un error",
        description:
          "Ha ocurrido un error al actualizar la descripción del viaje.",
        placement: "bottom",
        key: "global",
      });
    }
  };

  const onPlayManagerTrip = async () => {
    setLoading(true);
    try {
      await playManagerTrip(userRequest.bookingId!);
    } catch (e) {
      notification.error({
        message: "Error al reproducir el viaje",
        placement: "bottom",
      });
    } finally {
      setLoading(false);
    }
  };

  const saveScheduleDate = async () => {
    if (!userRequest.id) {
      return;
    }

    try {
      await updateSheduleDate(
        userRequest.id,
        scheduleDate.format("YYYY-MM-DD HH:mm:ss"),
      );
      notification.success({
        message: "Fecha de agendamiento actualizada correctamente!",
        placement: "bottom",
        key: "global",
      });
    } catch (error) {
      notification.error({
        message: "Ha ocurrido un error",
        description:
          "Ha ocurrido un error al actualizar la fecha de agendamiento del viaje.",
        placement: "bottom",
        key: "global",
      });
    }
  };

  return (
    <Card
      className="user-request-card"
      styles={{
        body: {
          padding: 0,
          background:
            userRequest.userTripNumber! <= 3
              ? isTextDarkMode
                ? "linear-gradient(45deg, #a28023 5%, #b19034 10%, #c0a044 30%, #cfb054 50%, #c0a044 70%, #b19034 80%, #a28023 95%)"
                : "linear-gradient(45deg, #d4af37 5%, #debc5b 10%, #e7c97c 30%, #efd69d 50%, #e7c97c 70%, #debc5b 80%, #d4af37 95%)"
              : isChecked
                ? isTextDarkMode
                  ? "rgb(99 99 99)"
                  : "#e5e5e5"
                : "",
        },
      }}
      hoverable
      onClick={(e) => onSelect(e, userRequest, !isChecked)}
    >
      <Row>
        <Col
          span={24}
          className="user-request-card-status"
          style={{ background: backgroundColor }}
        >
          <Row justify="center">
            <Text
              style={{
                color: AntDesignWhite,
              }}
              strong
            >
              {userRequestDtoStatus}
            </Text>
          </Row>
        </Col>
      </Row>
      <Row gutter={[0, 4]} className="user-request-card-fields">
        <Col span={22}>
          <Col>
            <CardItem
              isTextDarkMode={isTextDarkMode}
              src={HashtagIcon}
              text={userRequest.bookingId!}
              strong
            />
          </Col>
        </Col>
        <Col span={2}>
          <Space className="booking-id">
            <Tooltip title="Copiar al porta papeles">
              <Button
                shape="circle"
                icon={<PaperClipOutlined twoToneColor="#3b3c3c" />}
                onClick={async () =>
                  await generateClipboardText(
                    userRequest,
                    distinctPassengers,
                    true,
                  )
                }
              />
            </Tooltip>
          </Space>
        </Col>
        {isLote && (
          <Col span={24}>
            <Row align="middle" gutter={[6, 2]}>
              <Col span={12}>
                <CardItem
                  isTextDarkMode={isTextDarkMode}
                  src={UserIcon}
                  text={`${userRequest?.user?.firstName} ${userRequest?.user?.lastName}`}
                />
              </Col>
              <Col span={12}>
                <CardItem
                  isTextDarkMode={isTextDarkMode}
                  src={MobileIcon}
                  text={userRequest?.user?.mobile!}
                />
              </Col>
              <Col span={12}>
                <CardItem
                  isTextDarkMode={isTextDarkMode}
                  src={KmIcon}
                  text={String(userRequest?.distance)}
                />
              </Col>
            </Row>
          </Col>
        )}
        {!isLote && (
          <Col span={24}>
            <Row align="middle" justify="center">
              <Col span={12}>
                <CardItem
                  isTextDarkMode={isTextDarkMode}
                  src={UserIcon}
                  strong
                  text={
                    !!userRequest.passengers?.length
                      ? userRequest.passengers[0].name
                      : `${userRequest?.user?.firstName} ${userRequest?.user?.lastName}`
                  }
                />
              </Col>
              <Col span={12}>
                <CardItem
                  isTextDarkMode={isTextDarkMode}
                  src={MobileIcon}
                  strong
                  text={passengerPhone!}
                />
              </Col>
            </Row>
          </Col>
        )}
        <Col span={24}>
          <Row align="middle" justify="center">
            <Col span={12}>
              <Space>
                <Image
                  preview={false}
                  src={CompanyIcon}
                  alt=""
                  style={{
                    filter: isTextDarkMode ? IconDarkMode : "",
                  }}
                  width={20}
                />
                <Tooltip title={userRequest?.user?.admin?.name}>
                  <Text className="user-request-card-ellipsis" ellipsis>
                    Empresa: <b>{userRequest?.user?.admin?.name}</b>
                  </Text>
                </Tooltip>
              </Space>
            </Col>
            <Col span={12}>
              <Space>
                <Image
                  preview={false}
                  src={NoteIcon}
                  alt="Nota"
                  style={{
                    filter: isTextDarkMode ? IconDarkMode : "",
                    minWidth: "22.5px",
                  }}
                  width={20}
                />
                <Paragraph
                  editable={
                    userRequest.status === TRIP_STATUS.Scheduled && {
                      onChange: setEditableDescription,
                    }
                  }
                  ellipsis={{ rows: 2, tooltip: editableDescription }}
                  style={{ marginBottom: 0, maxWidth: "100%" }}
                >
                  {editableDescription}
                </Paragraph>
                {userRequest.status === TRIP_STATUS.Scheduled && (
                  <Tooltip title="Guardar cambios">
                    <Button
                      size="small"
                      disabled={
                        editableDescription === DEFAULT_DESCRIPTION ||
                        editableDescription === userRequest.description
                      }
                      type="text"
                      icon={<SaveTwoTone twoToneColor="#52c41a" />}
                      onClick={saveDescription}
                    />
                  </Tooltip>
                )}
              </Space>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <RouteSummary
            origin={userRequest.originAddress!}
            destination={destinationAddress}
            destinations={userRequest.destinations}
            fallbackDestination={userRequest.destinationAddress}
            isTextDarkMode={isTextDarkMode}
          />
        </Col>
        <Col span={24}>
          <Row align="middle">
            <Col span={24}>
              <CardItem
                isTextDarkMode={isTextDarkMode}
                src={CarIcon}
                text={`${userRequest?.serviceType?.name}`}
                strong
                isUrgent={userRequest?.serviceType?.name?.includes("Urgente")}
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row align="middle">
            <Col span={12}>
              <CardItem
                isTextDarkMode={isTextDarkMode}
                src={ProviderIcon}
                text={
                  userRequest.provider
                    ? `${userRequest?.provider?.firstName} ${userRequest?.provider?.lastName}`
                    : "No hay un conductor asignado"
                }
                strong
              />
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          <Row align="middle">
            <Col span={12}>
              <Space>
                <Image
                  preview={false}
                  src={Calendar}
                  alt=""
                  style={{
                    filter: isTextDarkMode ? IconDarkMode : "",
                  }}
                  width={20}
                />

                {userRequest.status === TRIP_STATUS.Scheduled && (
                  <>
                    <DatePicker
                      className="calendar"
                      disabledDate={disabledDate}
                      showTime
                      format="YYYY-MM-DD HH:mm"
                      placeholder="Seleccionar fecha"
                      allowClear={false}
                      value={scheduleDate}
                      onChange={(value) => setScheduleDate(value)}
                    />
                    <Tooltip title="Guardar cambios">
                      <Button
                        disabled={scheduleDate.isSame(
                          dayjs(userRequest.scheduleAt),
                        )}
                        type="text"
                        icon={<SaveTwoTone twoToneColor="#52c41a" />}
                        onClick={saveScheduleDate}
                      />
                    </Tooltip>
                  </>
                )}

                {userRequest.status !== TRIP_STATUS.Scheduled && (
                  <Text>
                    {userRequest.startedAt
                      ? dayjs(userRequest.startedAt).format("YYYY-MM-DD HH:mm")
                      : scheduleDate?.format("YYYY-MM-DD HH:mm")}
                  </Text>
                )}
              </Space>
            </Col>
          </Row>
        </Col>
        <Col span={24}>
          {(showPartner || showInternational) && (
            <Space wrap>
              {showPartner && (
                <Tag color="green">
                  Partner{" "}
                  <Image
                    src={HotelIcon}
                    alt="hotel"
                    style={{ filter: iconFilter }}
                    width={16}
                    preview={false}
                  />
                </Tag>
              )}
              {showInternational && (
                <Tag color="magenta">
                  Internacional · {userRequest.country!.name}
                </Tag>
              )}
            </Space>
          )}
        </Col>
        <Col span={24}>
          <TripExtraDetails
            userRequest={userRequest}
            isTextDarkMode={isTextDarkMode}
            isLote={isLote}
            passengerNames={distinctPassengers.map((p) => p.name)}
          />
        </Col>
        <Col span={24}>
          <Row align="middle">
            <Col span={12}>
              {userRequest.emptyTrip ? (
                <EmptyTripDetails emptyTrip={userRequest.emptyTrip} />
              ) : (
                <CreateEmptyTrip userRequest={userRequest} />
              )}
            </Col>
            {playableStatus.some((x) => x === userRequest.status) && (
              <Col span={12}>
                <Button
                  type="primary"
                  icon={<CaretRightOutlined />}
                  onClick={() => onPlayManagerTrip()}
                  disabled={userRequest?.isTripPlaying ?? false}
                  loading={loading}
                  color="green"
                  variant="solid"
                >
                  Reproducir viaje
                </Button>
              </Col>
            )}
          </Row>
        </Col>
      </Row>
    </Card>
  );
};

export default UserRequestCard;
